"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  useCallback,
  useState,
  useTransition,
  type FormEvent,
  type ReactNode,
} from "react";
import { Bell, ChevronDown, NotebookTabs } from "lucide-react";
import {
  PushSubscribeButton,
  type PushDeviceSubscriptionState,
} from "@/app/_components/PushSubscribeButton";
import { saveDailyBriefingPreferences } from "@/app/actions/briefing-preferences";
import { buildCompanionAlertsView, type CompanionAlertsView } from "../companion-home-state";
import { buildBriefingPushDeviceView } from "../briefing-settings-state";
import { SquadSocialSettingsForm } from "../SquadManagementCard";
import { queueCompanionRefresh } from "./CompanionPlanWorkspace";
import type { CompanionWorkspaceProps } from "./companion-workspace-types";

export function CompanionSettingsWorkspace({
  data,
  model,
}: CompanionWorkspaceProps): ReactNode {
  const alertsView = buildCompanionAlertsView(data, model);

  return (
    <div className="space-y-5">
      <section
        aria-labelledby="bases-settings-heading"
        className="rounded-md border border-border-light bg-white p-4 md:p-5"
      >
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
          Bases and trips
        </p>
        <h2
          id="bases-settings-heading"
          className="font-serif text-[26px] leading-tight text-navy"
        >
          Where Companion follows you
        </h2>
        <p className="mt-2 text-[12.5px] leading-relaxed text-text-muted">
          Set a home base or dated trip so Companion can keep the right places in context.
        </p>
        <button
          type="button"
          onClick={() =>
            window.dispatchEvent(new Event("companion:open-base-sheet"))
          }
          className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-4 py-2 text-[13px] font-semibold text-white hover:bg-navy/90"
        >
          Manage bases and trips
        </button>
      </section>

      <AlertsSection
        view={alertsView}
        isLoggedIn={Boolean(data.user)}
      />

      <section
        aria-labelledby="squad-social-settings-heading"
        className="rounded-md border border-border-light bg-white p-4 md:p-5"
      >
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
          Squad privacy
        </p>
        <h2
          id="squad-social-settings-heading"
          className="font-serif text-[26px] leading-tight text-navy"
        >
          Sharing and Social notifications
        </h2>
        <p className="mt-2 text-[12.5px] leading-relaxed text-text-muted">
          Choose separately what each Squad can see and how its updates reach you.
        </p>
        {data.squads.length > 0 ? (
          <div className="mt-4 grid gap-3">
            {data.squads.map((squad) => (
              <SquadSocialSettingsForm key={squad.id} squad={squad} />
            ))}
          </div>
        ) : (
          <p className="mt-4 rounded-sm border border-dashed border-border bg-warm-bg px-3 py-4 text-[12.5px] text-text-muted">
            Join or create a Squad to set sharing and Social notification preferences.
          </p>
        )}
      </section>

      <section
        aria-labelledby="companion-tools-heading"
        className="rounded-md border border-border-light bg-white p-4 md:p-5"
      >
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
          Companion tools
        </p>
        <h2
          id="companion-tools-heading"
          className="font-serif text-[26px] leading-tight text-navy"
        >
          Alerts and offline days
        </h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <SettingsLink
            href="/companion/briefings"
            label="Briefings"
            detail="Read your Morning Briefing history."
          />
          <SettingsLink
            href="/companion/calls"
            label="Calls"
            detail="Manage followed places and rare-condition alerts."
          />
          <SettingsLink
            href="/companion/offline"
            label="Offline packs"
            detail="Prepare supported resort packs before signal drops."
          />
        </div>
      </section>

      <section
        aria-labelledby="privacy-device-heading"
        className="rounded-md border border-border-light bg-white p-4 md:p-5"
      >
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
          Privacy and device
        </p>
        <h2
          id="privacy-device-heading"
          className="font-serif text-[26px] leading-tight text-navy"
        >
          Your recording controls
        </h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <SettingsLink
            href="/companion/history"
            label="Privacy and session data"
            detail="Review or delete local and privately synced sessions."
          />
          <SettingsLink
            href="/companion/track"
            label="Native recording and fall detection"
            detail="Open tracker setup for device permissions and available safety controls."
          />
        </div>
      </section>

    </div>
  );
}

function SettingsLink({
  href,
  label,
  detail,
}: {
  href: string;
  label: string;
  detail: string;
}): ReactNode {
  return (
    <Link
      href={href}
      className="flex min-h-[44px] flex-col justify-center rounded-sm border border-border-light bg-warm-bg px-3 py-3 no-underline hover:border-gold/50"
    >
      <span className="text-[13px] font-semibold text-navy">{label}</span>
      <span className="mt-1 text-[11.5px] leading-snug text-text-muted">
        {detail}
      </span>
    </Link>
  );
}

export function AlertsSection({
  view,
  isLoggedIn,
}: {
  view: CompanionAlertsView;
  isLoggedIn: boolean;
}): ReactNode {
  return (
    <section aria-labelledby="alerts-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
        Alerts
      </p>
      <h2 id="alerts-heading" className="font-serif text-[26px] leading-tight text-navy">
        Briefings and The Call
      </h2>
      <div className="mt-4 divide-y divide-border-light border-y border-border-light">
        <article className="py-4" aria-labelledby="briefing-alert-heading">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-warm-bg text-navy">
              <NotebookTabs className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              {view.briefing.state === "present" ? (
                <>
                  <h3 id="briefing-alert-heading" className="text-[14px] font-semibold leading-snug text-navy">
                    {view.briefing.title}
                  </h3>
                  <p className="mt-1 text-[12px] font-semibold text-text-muted">
                    {view.briefing.dateLabel}
                  </p>
                  <Link
                    href={view.briefing.href}
                    className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white no-underline hover:bg-navy/90"
                  >
                    {view.briefing.readLabel}
                  </Link>
                </>
              ) : (
                <>
                  <h3 id="briefing-alert-heading" className="text-[14px] font-semibold leading-snug text-navy">
                    {view.briefing.title}
                  </h3>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-text-muted">
                    {view.briefing.body}
                  </p>
                </>
              )}
            </div>
          </div>

          <details
            id="briefing-settings"
            className="mt-4 scroll-mt-6 rounded-sm border border-border-light bg-warm-bg"
          >
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-[12px] font-semibold text-navy">
              <span>{view.settings.title}</span>
              <ChevronDown className="h-4 w-4 text-text-muted" aria-hidden="true" />
            </summary>
            <BriefingSettingsForm settings={view.settings} isLoggedIn={isLoggedIn} />
          </details>
        </article>

        <article className="py-4" aria-labelledby="call-alert-heading">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-warm-bg text-navy">
              <Bell className="h-4 w-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 id="call-alert-heading" className="text-[14px] font-semibold leading-snug text-navy">
                {view.call.countLabel}
              </h3>
              <p className="mt-1 text-[12.5px] leading-relaxed text-text-muted">
                {view.call.latestLine}
              </p>
              <Link
                href={view.call.manageHref}
                className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
              >
                {view.call.manageLabel}
              </Link>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

function BriefingSettingsForm({
  settings,
  isLoggedIn,
}: {
  settings: CompanionAlertsView["settings"];
  isLoggedIn: boolean;
}): ReactNode {
  const router = useRouter();
  const [briefingEnabled, setBriefingEnabled] = useState(settings.enabled);
  const [selectedHour, setSelectedHour] = useState(settings.selectedHour);
  const [emailChecked, setEmailChecked] = useState(settings.emailChecked);
  const [pushChecked, setPushChecked] = useState(settings.pushChecked);
  const [deviceState, setDeviceState] = useState<PushDeviceSubscriptionState>({
    supported: false,
    subscribed: false,
    endpoint: null,
  });
  const [status, setStatus] = useState<{ tone: "success" | "error"; message: string } | null>(
    null,
  );
  const [pending, startTransition] = useTransition();
  const pushDeviceView = buildBriefingPushDeviceView({
    storedPushConsent: pushChecked,
    deviceSupported: deviceState.supported,
    deviceSubscribed: deviceState.subscribed && Boolean(deviceState.endpoint),
  });
  const pushToggleDisabled =
    pending || !briefingEnabled || (!pushDeviceView.devicePushReady && !pushChecked);
  const handleDeviceStateChange = useCallback((next: PushDeviceSubscriptionState) => {
    setDeviceState(next);
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(null);

    startTransition(() => {
      void (async () => {
        const result = await saveDailyBriefingPreferences({
          briefingEnabled,
          localSendTime: selectedHour,
          emailBriefingConsent: emailChecked,
          pushBriefingConsent: pushChecked,
          pushSubscriptionEndpoint: deviceState.endpoint,
        });

        if (!result.success) {
          setStatus({ tone: "error", message: result.error });
          return;
        }

        setStatus({ tone: "success", message: "Briefing settings saved." });
        queueCompanionRefresh(router);
      })();
    });
  }

  return (
    <form className="border-t border-border-light px-3 py-3" onSubmit={handleSubmit}>
      <label className="flex min-h-[44px] items-center gap-3 text-[13px] font-semibold text-navy">
        <input
          type="checkbox"
          checked={briefingEnabled}
          onChange={(event) => {
            const enabled = event.currentTarget.checked;
            setBriefingEnabled(enabled);
            if (!enabled) {
              setEmailChecked(false);
              setPushChecked(false);
            }
          }}
          disabled={pending}
          className="h-4 w-4 accent-navy disabled:cursor-not-allowed"
        />
        {settings.enabledToggleLabel}
      </label>
      <label className="block text-[12px] font-semibold text-navy" htmlFor="briefing-send-hour">
        {settings.hourLabel}
      </label>
      <select
        id="briefing-send-hour"
        value={selectedHour}
        onChange={(event) =>
          setSelectedHour(
            event.currentTarget.value as CompanionAlertsView["settings"]["selectedHour"],
          )
        }
        disabled={pending || !briefingEnabled}
        className="mt-2 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 py-2 text-[13px] font-semibold text-navy disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-text-muted"
      >
        {settings.allowedSendHours.map((hour) => (
          <option key={hour} value={hour}>
            {hour}
          </option>
        ))}
      </select>
      <div className="mt-3 space-y-2">
        <label className="flex min-h-[44px] items-center gap-3 text-[13px] font-semibold text-navy">
          <input
            type="checkbox"
            checked={emailChecked}
            onChange={(event) => setEmailChecked(event.currentTarget.checked)}
            disabled={pending || !briefingEnabled}
            className="h-4 w-4 accent-navy disabled:cursor-not-allowed"
          />
          {settings.emailToggleLabel}
        </label>
        <label className="flex min-h-[44px] items-center gap-3 text-[13px] font-semibold text-navy">
          <input
            type="checkbox"
            checked={pushChecked}
            onChange={(event) => {
              const nextChecked = event.currentTarget.checked;
              if (nextChecked && !pushDeviceView.devicePushReady) return;
              setPushChecked(nextChecked);
            }}
            disabled={pushToggleDisabled}
            className="h-4 w-4 accent-navy disabled:cursor-not-allowed"
          />
          {settings.pushToggleLabel}
        </label>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <span className="rounded-sm border border-border-light bg-white px-2 py-1 text-[12px] font-semibold text-navy">
          {pushDeviceView.preferenceLabel}
        </span>
        <span className="rounded-sm border border-border-light bg-white px-2 py-1 text-[12px] font-semibold text-text-muted">
          {pushDeviceView.deviceLabel}
        </span>
      </div>
      {!pushDeviceView.devicePushReady ? (
        <p className="mt-2 text-[12px] leading-relaxed text-text-muted">
          {settings.deviceNote}
        </p>
      ) : null}
      <div className="mt-3">
        <PushSubscribeButton
          isLoggedIn={isLoggedIn}
          onDeviceStateChange={handleDeviceStateChange}
        />
      </div>
      <p className="mt-3 text-[12px] leading-relaxed text-text-muted">
        {settings.promiseLine}
      </p>
      <p className="mt-1 text-[12px] leading-relaxed text-text-muted">
        {settings.note}
      </p>
      <button
        type="submit"
        disabled={pending}
        className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-4 py-2 text-[12px] font-semibold text-white transition-colors hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {pending ? "Saving" : "Save briefing settings"}
      </button>
      {status ? (
        <p
          className={`mt-3 text-[12px] leading-relaxed ${
            status.tone === "error" ? "text-rose-700" : "text-emerald-700"
          }`}
        >
          {status.message}
        </p>
      ) : null}
    </form>
  );
}
